import React from 'react';
import { Flex, Text } from 'rebass';
import Button from '@/components/Button/Button';
import { Label } from '@/components/Form';
import Form from '@/components/Form/Form';
import FormColumn from '@/components/Form/FormColumn';
import FormRow from '@/components/Form/FormRow';
import { ModalBody, ModalFooter } from '@/components/Modal';
import useVerificationChallenge, {
  VerificationChallenge,
} from '@/hooks/useVerificationChallenge';
import {
  codeSentMessage,
  resendLabel,
  switchLabel,
} from '@ecosconnect/core/src/auth/client';
import type {
  Challenge,
  VerificationPurpose,
  VerifiedResult,
} from '@ecosconnect/core/src/auth/types';
import ErrorText from '@/components/Form/ErrorText';
import CodeInput from './CodeInput';

const VerificationCodeFields: React.FC<{
  id: string;
  challenge: VerificationChallenge;
}> = ({ id, challenge }) => {
  return (
    <>
      <Text variant="body2" color="fieldBorderHover">
        {codeSentMessage(challenge.challenge)}
      </Text>
      <FormRow>
        <FormColumn>
          <Flex justifyContent="space-between" alignItems="baseline">
            <Label htmlFor={id} width="auto">
              Verification code
            </Label>
            <Button
              variant="link"
              onClick={challenge.resend}
              disabled={!challenge.canResend}
            >
              {resendLabel(
                challenge.secondsUntilResend,
                challenge.resendBlocked
              )}
            </Button>
          </Flex>
          <CodeInput challenge={challenge} id={id} width="100%" />
          {challenge.alternative && (
            <Button
              variant="link"
              onClick={challenge.switchMethod}
              disabled={!!challenge.pending}
              mt={2}
            >
              {switchLabel(challenge.alternative)}
            </Button>
          )}
        </FormColumn>
      </FormRow>
      <ErrorText message={challenge.error} />
    </>
  );
};

type VerificationCodeStepProps<P extends VerificationPurpose> = {
  id: string;
  purpose: P;
  challenge: Challenge;
  confirmLabel: string;
  onVerified: (result: VerifiedResult[P]) => unknown;
  onBack: (message: string) => void;
};

export const VerificationCodeStep = <P extends VerificationPurpose>({
  id,
  purpose,
  challenge,
  confirmLabel,
  onVerified,
  onBack,
}: VerificationCodeStepProps<P>) => {
  const verification = useVerificationChallenge({
    purpose,
    challenge,
    onVerified,
    onExit: onBack,
  });

  return (
    <>
      <ModalBody>
        <Form onSubmit={verification.verify}>
          <VerificationCodeFields id={id} challenge={verification} />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          onClick={verification.verify}
          disabled={!verification.canSubmit}
        >
          {confirmLabel}
        </Button>
        <Button
          variant="outline"
          onClick={() => onBack('')}
          disabled={!!verification.pending}
        >
          Back
        </Button>
      </ModalFooter>
    </>
  );
};
